import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Building2, MapPin, Mail, Phone, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

export default function OfficeProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    api.get(`/offices/${id}`)
      .then(({ data: response }) => { if (live) setData(response); })
      .catch((requestError) => {
        if (live) {
          setData(null);
          setError(requestError?.response?.status === 404 ? "This office could not be found." : formatApiError(requestError));
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id, reloadKey]);

  // Match the final profile structure while loading to avoid a blank page and
  // large layout shift when office, agent, and listing data arrive together.
  if (loading) return (
    <div className="min-h-[65vh] bg-background" aria-label="Loading office profile">
      <section className="bg-secondary py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-start gap-6">
          <Skeleton className="h-20 w-20 rounded-2xl shrink-0" />
          <div className="flex-1 space-y-4"><Skeleton className="h-3 w-32" /><Skeleton className="h-12 max-w-xl" /><Skeleton className="h-4 max-w-2xl" /></div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[0, 1, 2].map((card) => <Skeleton key={card} className="h-80 rounded-2xl" />)}
      </div>
    </div>
  );
  if (error || !data) return (
    <main className="min-h-[65vh] bg-background px-6 py-20 grid place-items-center" role="alert">
      <div className="max-w-md text-center rounded-3xl border border-destructive/30 bg-destructive/5 p-8">
        <Building2 className="h-12 w-12 mx-auto text-destructive/70 mb-4" />
        <h1 className="font-display text-2xl font-bold text-primary">Office unavailable</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error || "This office could not be loaded."}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button type="button" variant="outline" onClick={() => setReloadKey((key) => key + 1)}>
            <RefreshCw className="h-4 w-4 mr-2" /> Try again
          </Button>
          <Button asChild><Link to="/offices">Browse offices</Link></Button>
        </div>
      </div>
    </main>
  );
  // WHY these fallbacks exist:
  //   The original code did `const { office, agents, properties } = data`.
  //   Because the old backend returned a bare office row instead of the
  //   wrapped { office, agents, properties } object, this destructure gave
  //   `office === undefined` and the render threw on `office.office_name`.
  //   The backend is now fixed to return the wrapped shape, but we still
  //   normalise both shapes so old-cached data and unit tests don't break.
  //   `data.office || data` picks whichever object actually has the fields.
  const office = data.office || data || {};
  const agents = data.agents || [];
  const properties = data.properties || [];
  const officeName = office.office_name || office.name || "Office name unavailable";
  const officeAddress = office.address || office.locality;
  const officeState = office.state || (office.raw && office.raw.state);
  const officeDescription = office.description || (office.raw && office.raw.description);
  const officePhone = office.contact_phone || office.phone;
  const officeEmail = office.contact_email || office.email;

  return (
    <div className="bg-background min-h-screen" data-testid="office-profile-page">
      <section className="bg-secondary py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-border ring-1 ring-primary/10 grid place-items-center text-primary shadow-sm animate-gradient-pan"><Building2 className="w-8 h-8"/></div>
            <div className="flex-1 min-w-0 w-full sm:w-auto">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Registered Office</p>
              <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter text-primary break-words">{officeName}</h1>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-primary">
                {[officeAddress, office.city, officeState].filter(Boolean).length > 0 && <span className="flex items-start gap-1.5 font-semibold min-w-0"><MapPin className="w-4 h-4 shrink-0 mt-0.5"/><span className="break-words">{[officeAddress, office.city, officeState].filter(Boolean).join(", ")}</span></span>}
                {officePhone && <a href={`tel:${officePhone}`} className="flex items-center gap-1.5 font-semibold break-all"><Phone className="w-4 h-4 shrink-0"/>{officePhone}</a>}
                {officeEmail && <a href={`mailto:${officeEmail}`} className="flex items-center gap-1.5 font-semibold break-all"><Mail className="w-4 h-4 shrink-0"/>{officeEmail}</a>}
              </div>
            </div>
          </div>
          {officeDescription && <p className="text-muted-foreground mt-6 max-w-3xl leading-relaxed">{officeDescription}</p>}
        </div>
      </section>

      {agents?.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="font-display text-3xl font-bold text-primary mb-8">Agents at {officeName}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {agents.map(a => (
                <Link key={a.user_id} to={`/agents/${a.user_id}`} className="bg-background border border-border rounded-lg p-5 hover:shadow-md hover:border-primary/30 hover:-translate-y-0.5 transition-all duration-200 group">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/15 to-secondary overflow-hidden mb-3 ring-2 ring-primary/10 grid place-items-center text-primary font-display font-black group-hover:scale-105 group-hover:ring-primary/30 transition-all duration-300">{a.picture ? <img src={a.picture} alt={a.name} loading="lazy" decoding="async" className="w-full h-full object-cover"/> : (a.name ? a.name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase() : "SH")}</div>
                  <p className="font-display font-bold text-primary">{a.name}</p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">
                    {a.experience_years ? `${a.experience_years}y experience` : "Experience unavailable"} · {a.city || "Location unavailable"}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="font-display text-3xl font-bold text-primary mb-8">Listings</h2>
          {properties.length === 0 ? (
            <p className="text-muted-foreground">No active listings.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map(p => <PropertyCard key={p.property_id} property={p}/>)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
